import { useState, useMemo, useEffect } from "react";
import { deleteDonor } from "../../../../api/DeleteDonor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSliders, faEllipsis } from "@fortawesome/free-solid-svg-icons";
import FilterDrawer from "../../../shared/FilterDrawer/FilterDrawer";
import DataTable from "../../../shared/DataTable/DataTable";

const DONOR_FILTERS = [
  {
    label: "Source",
    key: "source",
    options: [
      { label: "Upload", value: "upload" },
      { label: "Manual", value: "manual" },
      { label: "Donation", value: "donation" },
    ],
  },
];

const DonorTable = ({ nonprofitId, data, serverPagination, serverPageCount, serverTotalCount, onServerPageChange }) => {
  const [localData, setLocalData] = useState(data);
  const [selectedIds, setSelectedIds] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(-1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const filteredData = useMemo(() => {
    const sourceFilter = appliedFilters.source || [];
    return localData.filter(
      (d) => sourceFilter.length === 0 || sourceFilter.includes(d.source)
    );
  }, [localData, appliedFilters]);

  const donorDeleteButtonCallback = (donorId) => {
    try {
      deleteDonor(donorId);
      setLocalData((prev) => prev.filter((d) => d.id !== donorId));
      setConfirmDelete(-1);
      setOpenMenuId(null);
    } catch (error) {
      console.error("Error deleting donor:", error);
    }
  };

  const columns = useMemo(() => [
    {
      id: "select",
      header: () => null,
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(row.original.id)}
          onChange={() =>
            setSelectedIds((prev) =>
              prev.includes(row.original.id)
                ? prev.filter((x) => x !== row.original.id)
                : [...prev, row.original.id]
            )
          }
          className="w-4 h-4 rounded border-gray-300 accent-blue cursor-pointer"
        />
      ),
      enableSorting: false,
      enableGlobalFilter: false,
      size: 40,
    },
    { accessorKey: "first_name", header: "First Name" },
    { accessorKey: "last_name", header: "Last Name" },
    { accessorKey: "source", header: "Source" },
    { accessorKey: "email", header: "Email" },
    {
      accessorKey: "phone",
      header: "Phone Number",
      cell: ({ getValue }) => getValue() || "",
    },
    {
      accessorKey: "donations",
      header: "Donations",
      cell: ({ getValue }) => getValue() || "",
    },
    {
      id: "actions",
      header: () => null,
      cell: ({ row }) => {
        const donor = row.original;
        return (
          <div className="text-center relative">
            {confirmDelete === donor.id ? (
              <div className="flex gap-1 justify-center">
                <button
                  onClick={() => donorDeleteButtonCallback(donor.id)}
                  className="text-xs text-white bg-red-500 px-2 py-1 rounded-full hover:bg-red-600"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setConfirmDelete(-1)}
                  className="text-xs text-gray-600 border border-gray-300 px-2 py-1 rounded-full hover:bg-gray-100"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() =>
                    setOpenMenuId(openMenuId === donor.id ? null : donor.id)
                  }
                  className="text-gray-500 hover:text-gray-800 bg-transparent border-none outline-none"
                >
                  <FontAwesomeIcon icon={faEllipsis} />
                </button>
                {openMenuId === donor.id && (
                  <div className="absolute right-0 top-8 z-10 bg-white border border-gray-200 rounded-xl shadow-lg py-1 min-w-[120px]">
                    <button
                      onClick={() => {
                        setConfirmDelete(donor.id);
                        setOpenMenuId(null);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-gray-50"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      },
      enableSorting: false,
      enableGlobalFilter: false,
      size: 40,
    },
  ], [selectedIds, openMenuId, confirmDelete]);

  const filterSlot = (
    <>
      <button
        onClick={() => setFilterOpen(true)}
        className="flex items-center gap-2 border border-gray-300 rounded-full px-4 py-2 text-sm text-gray-700 bg-white hover:bg-gray-50 whitespace-nowrap"
      >
        <FontAwesomeIcon icon={faSliders} size="sm" />
        Filter
      </button>
      <FilterDrawer
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={DONOR_FILTERS}
        appliedFilters={appliedFilters}
        onApply={(filters) => setAppliedFilters(filters)}
      />
    </>
  );

  return (
    <DataTable
      columns={columns}
      data={filteredData}
      filterPlaceholder="Search donors"
      filterSlot={filterSlot}
      totalLabel="Total Donors"
      emptyMessage="No donors found."
      serverPagination={serverPagination}
      serverPageCount={serverPageCount}
      serverTotalCount={serverTotalCount}
      onServerPageChange={onServerPageChange}
    />
  );
};

export default DonorTable;
